import {EventDispatcher} from './EventDispatcher.js';
import {
  COMPLETE,
  CHANGE,
  FAIL
} from './constants.js';
import {log} from './logfunctions.js';

/**
 * An individual task to be executed by a TaskManager or individually by manually invoking start().
 */
export class Task extends EventDispatcher {
  /**
   * @param maxRetryAttempts The number of times to retry the Task before giving up. When all retry
   *                         attempts have completed, the Task dispatches the COMPLETE event. When
   *                         maxRetryAttempts is 0 (default), the Task is attempted once only.
   *
   * @param retryDelay The duration to wait between retry attempts. Defaults to 0 (retry immediately).
   */
  constructor (maxRetryAttempts = 0, retryDelay = 0) {
    super();
    this.name = '';
    this.complete = false;
    this.taskHasNeverStarted = true;
    this.failed = false;
    this.maxRetryAttempts = maxRetryAttempts;
    this.numRetryAttempts = 0;
    this.retryDelay = retryDelay;
    this.retryTimeoutID = -1;
    this.startTime = 0;
    this.progress = 0;  // Not supported by all Task subclasses
    this.executionDuration = 0;
  }

  /**
   * Begins the task. Subclasses provide concrete implementations for this "abstract" method.
   */
  start () {
    // As a precaution, abort any retry attempt that might be pending. Technically, start() should
    // never be invoked more than once per Task object, but in the event that start() is invoked
    // multiple times, this clearTimeout() prevents resource leakage and possible unexpected
    // redundant task execution.
    clearTimeout(this.retryTimeoutID);

    if (this.taskHasNeverStarted) {
      log(`Task start: [${this.name}]`);
      this.taskHasNeverStarted = false;
      this.startTime = new Date();
      this.complete = false;
    } else {
      throw new Error(`Task [${this.name}] invoked start() more than once. The start() method must`
        + ' not be invoked more than once per Task object. To perform a task multiple times, use'
        + ' multiple, separate Task objects. To retry a Task that fails, use Task\'s constructor\'s'
        + ' maxRetryAttempts parameter.');
    }
  }

  /**
   * Retries the task. Specifically, if this Task is configured to retry upon failure, and this task
   * fails, then the retry() method is invoked automatically (by the fail() method). Subclasses must
   * provide the concrete implementation for this "abstract" method.
   *
   * Example subclass retry() method implementation:
   *   retry () {
   *     super.retry();
   *     this.doTask();  // doTask() implemented by subclass
   *   }
   */
  retry () {
    this.numRetryAttempts++;
    log(`Task [${this.name}] didn't complete successfully. Retrying. Retry attempt number [${this.numRetryAttempts}].`);
  }

  /**
   * Sets and broadcasts the amount of the task that has been completed, as a percentage. Subclasses
   * determine how to measure the percentage; however, subclasses are not obliged to report progress.
   *
   * @param percent A floating point indicating progress as a percentage. Example: .5 (half done)
   */
  setProgress (percent) {
    if (!this.complete) {
      this.progress = percent;
      this.dispatchEvent(CHANGE, {progress: percent});
    } else {
      throw new Error(`Task [${this.name}] invoked progress() after completion.`);
    }
  }

  /**
   * Officially registers the task as completed. Invoked by subclasses based on implementation-specific
   * definition of "task completion."
   */
  done () {
    // When done() is called, the task has ended forever, so abort any in-progress retry attempt.
    clearTimeout(this.retryTimeoutID);

    if (!this.complete) {
      let now = new Date();
      this.executionDuration = now.getTime() - this.startTime.getTime();
      this.complete = true;
      log(`Task complete: [${this.name}] (duration: ${this.getTaskDurationString()}).`);
      this.dispatchEvent(COMPLETE);
    } else {
      throw new Error(`Task [${this.name}] invoked done() after completion.`);
    }
  }

  /**
   * Indicates that the task did not complete successfully. If the specified maxRetryAttempts has
   * not yet been reached, the task will be performed again (by executing retry()).
   *
   * Example:
   * someTask.fail(new Error('Could not complete task'));
   *
   * @param error An Error or Error subclass instance.
   */
  fail (error) {
    if (!this.complete) {
      if (this.numRetryAttempts < this.maxRetryAttempts) {
        if (this.retryDelay > 0) {
          // Retry after the Task's specified retryDelay duration elapses
          this.retryTimeoutID = setTimeout(() => {
            // If the task is already complete, don't retry. (Rationale: If the task was aborted
            // during the retry delay, it will now be complete, in which case no retry attempt
            // should be made.)
            if (!this.complete) {
              this.retry();
            }
          }, this.retryDelay);
        } else {
          // Retry immediately
          this.retry();
        }
      } else {
        // Task failed permanently. No further retries will be attempted.
        let now = new Date();
        this.executionDuration = now.getTime() - this.startTime.getTime();
        this.complete = true;
        this.failed = true;
        log(`Task failed: [${this.name}] (duration: ${this.getTaskDurationString()}).`);
        this.dispatchEvent(FAIL, {error: error});
      }
    } else {
      throw new Error(`Task [${this.name}] invoked fail() after completion.`);
    }
  }

  getTaskDurationString () {
    return this.executionDuration < 999 ? `${this.executionDuration}ms` : `${this.executionDuration/1000}s`;
  }
}
